import { format, parseISO, startOfToday, formatISO } from 'date-fns';
import apiClient from '../shared/apiClient';
import { 
  createFieldChange, 
  buildFieldChanges, 
  createAuditLogData,
  FIELD_MAPPINGS 
} from '../../utils/historyUtils';
import { 
  locationByClientId,
  orgUnitsByClientAndLevel,
  getLocation
} from 'graphql/queries';
import {
  createLocation,
  updateLocation
} from 'graphql/mutations';

/**
 * Service for managing location CRUD operations
 * Copied from locationAdminService.js with minimal changes
 */
class LocationService {
  // Get minimal location data for master list (highly optimized)
  async getLocationsMaster(clientId, orgUnits = null) {
    try {
      const startTime = performance.now();
      
      // Fetch locations with ONLY fields needed for table and counts
      const locationsResult = await apiClient.query(locationByClientId, { 
        clientId,
        limit: 2000  // Support larger datasets
      });
      
      const locations = locationsResult?.data?.locationByClientId?.items || [];
      
      // If orgUnits not provided, fetch them to get names
      let orgUnitMap = {};
      if (!orgUnits) {
        const orgUnitsResult = await apiClient.query(orgUnitsByClientAndLevel, { clientId });
        const fetchedOrgUnits = orgUnitsResult?.data?.orgUnitsByClientAndLevel?.items || [];
        fetchedOrgUnits.forEach(unit => {
          orgUnitMap[unit.id] = unit;
        });
      } else {
        // Use provided orgUnits
        orgUnits.forEach(unit => {
          orgUnitMap[unit.id] = unit;
        });
      }
      
      // Return minimal fields only with mapped org unit names
      const masterList = locations.map(loc => ({
        // Essential identifiers
        id: loc.id || loc.ID,
        locationId: loc.locationId || loc.id,
        
        // Display fields for table
        locationName: loc.name || loc.locationName,
        locationNumber: loc.locationNum || loc.locationNumber,
        city: loc.address?.city || loc.city,
        state: loc.address?.state || loc.state,
        
        // Status determination
        status: loc.closeDate ? 'Closed' : (loc.status || 'Active'),
        closeDate: loc.closeDate,
        
        // Org assignment (for counts and filtering)
        orgLevel_1: loc.orgLevel_1,
        orgLevel_1_name: orgUnitMap[loc.orgLevel_1]?.name || null,
        orgLevel_2: loc.orgLevel_2,
        orgLevel_2_name: orgUnitMap[loc.orgLevel_2]?.name || null,
        orgLevel_3: loc.orgLevel_3,
        orgLevel_3_name: orgUnitMap[loc.orgLevel_3]?.name || null,
        
        // Optional fields for display
        format: loc.format || 'Traditional',
        ownershipType: loc.ownershipType || '-'
      }));
      
      const fetchTime = performance.now() - startTime;

      return masterList;
    } catch (error) {
      console.error('Error fetching locations master:', error);
      throw error;
    }
  }

  // Get basic location data for table display (optimized for performance)
  async getLocationsBasicInfo(clientId) {
    try {
      // Fetch locations with only essential fields for table display
      const locationsResult = await apiClient.query(locationByClientId, { 
        clientId,
        limit: 1000
      });
      const locations = locationsResult?.data?.locationByClientId?.items || [];
      
      // Fetch all org units to get names
      const orgUnitsResult = await apiClient.query(orgUnitsByClientAndLevel, { clientId });
      const orgUnits = orgUnitsResult?.data?.orgUnitsByClientAndLevel?.items || [];
      
      // Create org unit map for quick lookup
      const orgUnitMap = {};
      orgUnits.forEach(unit => {
        orgUnitMap[unit.id] = unit;
      });
      
      // Return only essential fields for table display
      const basicLocations = locations.map(loc => {
        // Determine status based on closeDate
        let status = 'Active';
        if (loc.closeDate) {
          // Check if closeDate is in the past
          const closeDateObj = parseISO(loc.closeDate);
          const today = startOfToday(); // Reset time to start of day for accurate comparison
          
          if (closeDateObj <= today) {
            status = 'Closed';
          } else {
            status = 'Closing'; // Future close date
          }
        }
        
        return {
          id: loc.id,
          locationName: loc.name || loc.locationName,
          locationNumber: loc.locationNum || loc.locationNumber,
          city: loc.address?.city || loc.city,
          state: loc.address?.state || loc.state,
          status: status,
          closeDate: loc.closeDate, // Include closeDate for the component to use
          orgLevel_1: loc.orgLevel_1,
          orgLevel_2: loc.orgLevel_2,
          orgLevel_3: loc.orgLevel_3,
          orgLevel_1_name: orgUnitMap[loc.orgLevel_1]?.name || null,
          orgLevel_2_name: orgUnitMap[loc.orgLevel_2]?.name || null,
          orgLevel_3_name: orgUnitMap[loc.orgLevel_3]?.name || null
        };
      });

      return basicLocations;
    } catch (error) {
      console.error('❌ Error fetching locations basic info:', error);
      throw error;
    }
  }

  // Get full details for a specific location (called when user clicks on a location)
  async getLocationFullDetails(clientId, locationId) {
    try {
      // Fetch the specific location with all details using the generated query
      const locationResult = await apiClient.query(getLocation, { id: locationId });
      
      const location = locationResult?.data?.getLocation;
      
      if (!location) {
        throw new Error('Location not found');
      }
      
      // Fetch org unit names if needed
      const orgUnitIds = [location.orgLevel_1, location.orgLevel_2, location.orgLevel_3].filter(Boolean);
      const orgUnitMap = {};
      
      if (orgUnitIds.length > 0) {
        const orgUnitsResult = await apiClient.query(orgUnitsByClientAndLevel, { clientId });
        const orgUnits = orgUnitsResult?.data?.orgUnitsByClientAndLevel?.items || [];
        orgUnits.forEach(unit => {
          if (orgUnitIds.includes(unit.id)) {
            orgUnitMap[unit.id] = unit;
          }
        });
      }
      
      // Fetch manager assignments for this location
      // Note: This creates a circular dependency with managerService
      // For now, we'll import it dynamically
      const { default: managerService } = await import('./managerService');
      const managerAssignments = await managerService.getManagerAssignmentsForLocation(clientId, locationId, orgUnitIds);
      
      // Return enhanced location with all details
      return {
        ...location,
        locationName: location.name || location.locationName,
        locationNumber: location.locationNum || location.locationNumber,
        city: location.address?.city,
        state: location.address?.state,
        orgLevel_1_name: orgUnitMap[location.orgLevel_1]?.name || null,
        orgLevel_2_name: orgUnitMap[location.orgLevel_2]?.name || null,
        orgLevel_3_name: orgUnitMap[location.orgLevel_3]?.name || null,
        format: location.format || 'Traditional',
        status: location.closeDate ? 'Inactive' : 'Active',
        managerAssignments
      };
    } catch (error) {
      console.error('❌ Error fetching location full details:', error);
      throw error;
    }
  }

  // Legacy method - redirect to optimized basic info method
  async getLocationsWithDetails(clientId) {
    return this.getLocationsBasicInfo(clientId);
  }

  // Create a new location
  async createLocation(clientId, locationData, userId, userName) {
    try {
      console.log('📍 Creating location with data:', JSON.stringify({
        ...locationData,
        openDate: locationData.openDate ? `${locationData.openDate} (type: ${typeof locationData.openDate})` : 'null',
        closeDate: locationData.closeDate ? `${locationData.closeDate} (type: ${typeof locationData.closeDate})` : 'null'
      }, null, 2));
      
      // Generate a unique ID for the location
      const locationId = `loc_${clientId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Prepare the location input - only include fields that exist in the schema
      const locationInput = {
        id: locationId,
        clientId: clientId,
        locationName: locationData.locationName,
        locationNumber: locationData.locationNumber || null, // Optional field
        
        // Address information
        address: {
          street_1: locationData.address.street_1,
          street_2: locationData.address.street_2 || '',
          city: locationData.address.city,
          state: locationData.address.state,
          zipCode: locationData.address.zipCode,
          country: locationData.address.country || 'USA'
        },
        
        // Operational details
        timeZone: locationData.timeZone,
        openDate: (() => {
          const formatted = locationData.openDate ? this.formatDateForStorage(locationData.openDate) : format(new Date(), 'yyyy-MM-dd');
          console.log('📅 Formatted openDate:', formatted);
          return formatted;
        })(),
        closeDate: (() => {
          const formatted = locationData.closeDate ? this.formatDateForStorage(locationData.closeDate) : null;
          console.log('📅 Formatted closeDate:', formatted);
          return formatted;
        })(),
        ownershipType: locationData.ownershipType || null,
        conceptId: locationData.conceptId || null,
        
        // Organizational assignment
        orgLevel_1: locationData.orgLevel_1 || null,
        orgLevel_2: locationData.orgLevel_2 || null,
        orgLevel_3: locationData.orgLevel_3 || null,
        
        // Metadata field (can store additional data as JSON)
        metadata: JSON.stringify({
          orgLevel_1_name: locationData.orgLevel_1_name || '',
          orgLevel_2_name: locationData.orgLevel_2_name || '',
          orgLevel_3_name: locationData.orgLevel_3_name || '',
          createdBy: userId,
          createdByName: userName,
          createdAt: formatISO(new Date())
        })
      };

      // Create the location

      const result = await apiClient.mutate(createLocation, { input: locationInput });
      
      const createdLocation = result.data.createLocation;

      // Create audit log entry using the standard method
      try {
        await this.createAuditLog({
          clientId: clientId,
          entityType: 'Location',
          entityId: createdLocation.id,
          entityName: createdLocation.locationName,
          changeType: 'LOCATION_CREATION',
          changeSummary: `Created new location: ${createdLocation.locationName}`,
          changeReason: 'New location created',
          changedBy: userId,
          changedByName: userName,
          fieldChanges: [{
            fieldName: 'Location Created',
            newValue: createdLocation.locationName,
            newDisplayValue: `${createdLocation.locationName} (#${createdLocation.locationNumber || 'N/A'})`
          }]
        });

      } catch (auditError) {
        console.error('⚠️ Failed to create audit log:', auditError);
        // Don't fail the whole operation if audit log fails
      }

      return createdLocation;
    } catch (error) {
      console.error('❌ Error creating location:', error);
      throw error;
    }
  }

  // Update location basic information
  async updateLocationBasicInfo(locationId, updates, userId, userName) {
    try {
      // First fetch current location to get old values for comparison
      const currentLocationResult = await apiClient.query(getLocation, { id: locationId });
      const currentLocation = currentLocationResult?.data?.getLocation;

      // Build update data, only including defined fields
      const updateData = {
        id: locationId
      };

      // Only add fields that are defined in the GraphQL schema
      if (updates.locationName !== undefined) {
        updateData.locationName = updates.locationName;
      }
      if (updates.locationNumber !== undefined) {
        updateData.locationNumber = updates.locationNumber;
      }
      if (updates.address !== undefined) {
        updateData.address = updates.address;
      }
      // Note: phone field is not in the Location GraphQL schema
      // if (updates.phone !== undefined) {
      //   updateData.phone = updates.phone;
      // }
      if (updates.timeZone !== undefined) {
        updateData.timeZone = updates.timeZone;
      }

      const result = await apiClient.mutate(updateLocation, { input: updateData });

      // Use the utility to build field changes consistently
      const fieldChanges = buildFieldChanges(
        currentLocation,
        {
          locationName: updates.locationName,
          locationNumber: updates.locationNumber,
          address: updates.address,
          timeZone: updates.timeZone
        },
        FIELD_MAPPINGS.basicInformation
      );
      
      if (fieldChanges.length > 0) {
        const auditData = createAuditLogData({
          clientId: result.data.updateLocation.clientId,
          changeType: 'LOCATION_UPDATE',
          entityType: 'Location',
          entityId: locationId,
          entityName: result.data.updateLocation.locationName || `Location ${locationId}`,
          section: 'Basic Information',
          fieldChanges: fieldChanges,
          changeReason: updates.changeReason || 'Basic information updated',
          userId: userId,
          userName: userName
        });
        
        await this.createAuditLog(auditData);
      }

      return result.data.updateLocation;
    } catch (error) {
      console.error('❌ Error updating location basic info:', error);
      throw error;
    }
  }

  // Update location organizational assignment
  async updateLocationOrgAssignment(locationId, updates, userId, userName, clientId, orgUnits = []) {
    try {
      // First fetch current location to get old values for comparison
      const currentLocationResult = await apiClient.query(getLocation, { id: locationId });
      const currentLocation = currentLocationResult?.data?.getLocation;
      
      // Look up names for current org units from the passed orgUnits array
      let currentOrgLevel1Name = '';
      let currentOrgLevel2Name = '';
      let currentOrgLevel3Name = '';
      
      if (currentLocation?.orgLevel_1) {
        const orgUnit1 = orgUnits.find(u => u.id === currentLocation.orgLevel_1);
        currentOrgLevel1Name = orgUnit1?.name || '';
        if (!currentOrgLevel1Name) {
        }
      }
      
      if (currentLocation?.orgLevel_2) {
        const orgUnit2 = orgUnits.find(u => u.id === currentLocation.orgLevel_2);
        currentOrgLevel2Name = orgUnit2?.name || '';
        if (!currentOrgLevel2Name) {
        }
      }
      
      if (currentLocation?.orgLevel_3) {
        const orgUnit3 = orgUnits.find(u => u.id === currentLocation.orgLevel_3);
        currentOrgLevel3Name = orgUnit3?.name || '';
        if (!currentOrgLevel3Name) {
        }
      }

      // Only include the orgLevel IDs, not the names (names are computed fields)
      const updateData = {
        id: locationId
      };
      
      // Only add org level IDs if they are defined
      // Convert empty strings to null to avoid DynamoDB index key errors
      if (updates.orgLevel_1 !== undefined) {
        updateData.orgLevel_1 = updates.orgLevel_1 || null;
      }
      if (updates.orgLevel_2 !== undefined) {
        updateData.orgLevel_2 = updates.orgLevel_2 || null;
      }
      if (updates.orgLevel_3 !== undefined) {
        updateData.orgLevel_3 = updates.orgLevel_3 || null;
      }

      const result = await apiClient.mutate(updateLocation, { input: updateData });

      // Create hierarchy change log for org assignment change - only track actual changes
      const fieldChanges = [];
      
      // Check each org level for actual changes
      if (updates.orgLevel_1 !== undefined && updates.orgLevel_1 !== currentLocation?.orgLevel_1) {
        fieldChanges.push(createFieldChange(
          'Level 1 Assignment',
          currentOrgLevel1Name || currentLocation?.orgLevel_1 || 'None',
          updates.orgLevel_1_name || updates.orgLevel_1 || 'None',
          'text'
        ));
      }
      
      if (updates.orgLevel_2 !== undefined && updates.orgLevel_2 !== currentLocation?.orgLevel_2) {
        fieldChanges.push(createFieldChange(
          'Level 2 Assignment',
          currentOrgLevel2Name || currentLocation?.orgLevel_2 || 'None',
          updates.orgLevel_2_name || updates.orgLevel_2 || 'None',
          'text'
        ));
      }
      
      if (updates.orgLevel_3 !== undefined && updates.orgLevel_3 !== currentLocation?.orgLevel_3) {
        fieldChanges.push(createFieldChange(
          'Level 3 Assignment',
          currentOrgLevel3Name || currentLocation?.orgLevel_3 || 'None',
          updates.orgLevel_3_name || updates.orgLevel_3 || 'None',
          'text'
        ));
      }
      
      if (fieldChanges.length > 0) {
        // Build the list of affected org units (both old and new)
        const affectedOrgUnits = [
          updates.orgLevel_1,
          currentLocation?.orgLevel_1,
          updates.orgLevel_2,
          currentLocation?.orgLevel_2,
          updates.orgLevel_3,
          currentLocation?.orgLevel_3
        ].filter(id => id && id !== null && id !== '');
        
        // Use createAuditLogData to build consistent audit log data
        const auditData = createAuditLogData({
          clientId: clientId,
          changeType: 'ASSIGNMENT',
          entityType: 'Location',
          entityId: locationId,
          entityName: result.data.updateLocation.locationName || `Location ${locationId}`,
          section: 'Organizational Assignment',
          fieldChanges: fieldChanges,
          changeReason: updates.changeReason || 'Organizational reassignment',
          userId: userId,
          userName: userName,
          // Pass the affected org units for complete tracking
          affectedOrgUnits: affectedOrgUnits
        });
        
        // Import historyService to use its createAuditLog method
        const { default: historyService } = await import('./historyService');
        await historyService.createAuditLog(auditData);

      }

      return result.data.updateLocation;
    } catch (error) {
      console.error('❌ Error updating location org assignment:', error);
      throw error;
    }
  }

  // Update location operational status
  async updateLocationOperationalStatus(locationId, updates, userId, userName, clientId) {
    try {
      // First fetch current location to get old values
      const currentLocationResult = await apiClient.query(getLocation, { id: locationId });
      const currentLocation = currentLocationResult?.data?.getLocation;

      // Format dates as AWSDate (YYYY-MM-DD) not AWSDateTime
      const formatDate = (dateString) => {
        if (!dateString) return null;
        const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
        return format(date, 'yyyy-MM-dd'); // Get YYYY-MM-DD part only
      };

      // Build update data, only including defined fields
      const updateData = {
        id: locationId
      };

      // Only add fields that are defined
      if (updates.openDate !== undefined) {
        updateData.openDate = formatDate(updates.openDate);
      }
      if (updates.closeDate !== undefined) {
        updateData.closeDate = formatDate(updates.closeDate);
      }
      if (updates.ownershipType !== undefined) {
        updateData.ownershipType = updates.ownershipType;
      }
      if (updates.conceptId !== undefined) {
        updateData.conceptId = updates.conceptId;
      }

      const result = await apiClient.mutate(updateLocation, { input: updateData });

      // Create audit log for status change
      if (updates.changeReason || Object.keys(updates).some(key => key !== 'changeReason')) {
        const fieldChanges = [];
        
        // Track status change - only if it actually changed
        if (updates.closeDate !== undefined) {
          const oldCloseDate = currentLocation?.closeDate;
          const newCloseDate = updates.closeDate ? formatDate(updates.closeDate) : null;
          
          // Only add if there's an actual change
          if (oldCloseDate !== newCloseDate) {
            fieldChanges.push(createFieldChange(
              'Status',
              oldCloseDate ? `Closed (${oldCloseDate})` : 'Active',
              newCloseDate ? `Closed (${newCloseDate})` : 'Active',
              'text'
            ));
          }
        }
        
        // Track other field changes
        if (updates.ownershipType !== undefined && updates.ownershipType !== currentLocation?.ownershipType) {
          fieldChanges.push(createFieldChange(
            'Ownership Type',
            currentLocation?.ownershipType,
            updates.ownershipType,
            'text'
          ));
        }
        
        if (updates.conceptId !== undefined && updates.conceptId !== currentLocation?.conceptId) {
          fieldChanges.push(createFieldChange(
            'Concept',
            currentLocation?.conceptId,
            updates.conceptId,
            'text'
          ));
        }
        
        if (updates.openDate !== undefined && formatDate(updates.openDate) !== currentLocation?.openDate) {
          fieldChanges.push(createFieldChange(
            'Open Date',
            currentLocation?.openDate,
            formatDate(updates.openDate),
            'date'
          ));
        }

        // Only create audit log if there are actual changes
        if (fieldChanges.length > 0) {
          const auditData = createAuditLogData({
            clientId: clientId,
            changeType: 'LOCATION_UPDATE',
            entityType: 'Location',
            entityId: locationId,
            entityName: result.data.updateLocation.locationName || `Location ${locationId}`,
            section: 'Operational Status',
            fieldChanges: fieldChanges,
            changeReason: updates.changeReason || 'Status update',
            userId: userId,
            userName: userName
          });
          
          await this.createAuditLog(auditData);
        }
      }

      return result.data.updateLocation;
    } catch (error) {
      console.error('❌ Error updating location operational status:', error);
      throw error;
    }
  }

  // Helper method to format dates for storage
  formatDateForStorage(dateValue) {
    try {
      // If it's already a Date object
      if (dateValue instanceof Date) {
        if (isNaN(dateValue.getTime())) {
          console.error('Invalid Date object:', dateValue);
          return format(new Date(), 'yyyy-MM-dd');
        }
        return format(dateValue, 'yyyy-MM-dd');
      }
      
      // If it's a string, try to parse it
      if (typeof dateValue === 'string') {
        const parsedDate = parseISO(dateValue);
        if (isNaN(parsedDate.getTime())) {
          console.error('Invalid date string:', dateValue);
          return format(new Date(), 'yyyy-MM-dd');
        }
        return format(parsedDate, 'yyyy-MM-dd');
      }
      
      // If it's a number (timestamp)
      if (typeof dateValue === 'number') {
        return format(new Date(dateValue), 'yyyy-MM-dd');
      }
      
      // Default to today if we can't parse it
      console.warn('Unknown date format, using today:', dateValue);
      return format(new Date(), 'yyyy-MM-dd');
    } catch (error) {
      console.error('Error formatting date for storage:', error, 'Date value:', dateValue);
      return format(new Date(), 'yyyy-MM-dd');
    }
  }

  // Use createAuditLog from historyService for consistency
  async createAuditLog(logData) {
    const { default: historyService } = await import('./historyService');
    return historyService.createAuditLog(logData);
  }
}

export default new LocationService();